/**
 * The arithmetic behind the mission strips, kept pure so it can be tested.
 *
 * **Every timer here is derived from a tick, and none of them is stored.** The
 * wire carries `nextWaveAtTick` and `droppedAtTick` — instants, written once —
 * and the remaining time is a subtraction performed on read, exactly the way a
 * crate's descent and a research node's maturity are. Nothing in this file may
 * grow a counter that something else decrements, because two sides counting
 * down separately is two clocks to disagree, and the one the player believes is
 * whichever is on screen.
 *
 * It is separated from `GameHud.vue` for the reason `radar-geometry.ts` is
 * separated from `RadarScope.vue`: this is the half that can be wrong in a way
 * a screenshot cannot show. A countdown that is one wave out, or a "3 / 8" that
 * reads the cleared wave rather than the coming one, photographs perfectly.
 *
 * It sits in `game/` rather than in `ui/` because `ArenaScene` reads it too —
 * the scope's gate flash is derived from a tick, and the components already
 * import from `game/` while nothing in `game/` may import from `ui/`. Same
 * placement as `tutorial.ts` and for the same reason: pure arithmetic over wire
 * state, no Babylon, no DOM, no clock.
 */

import {
  CTF_FLAG_RETURN_TICKS,
  TICK_RATE,
  WAVE_COUNT,
  type ConvoyStatus,
} from '@shared/protocol';

/**
 * Whole seconds from now until `targetTick`, never negative.
 *
 * Rounded **up**, so a countdown reads "1" for the whole of its last second and
 * reaches "0" only when the thing has actually happened — a timer that shows 0
 * while there is still a third of a second to run is a timer players report as
 * stuck.
 *
 * A tick in the past answers 0 rather than a negative number: a client whose
 * snapshot is a tick behind the instant it is measuring against is the ordinary
 * case, not an error.
 */
export function secondsUntil(targetTick: number, nowTick: number): number {
  return Math.max(0, Math.ceil((targetTick - nowTick) / TICK_RATE));
}

/**
 * `m:ss`, for a countdown that is read at a glance.
 *
 * Seconds are zero-padded and minutes are not, so the string never changes
 * width mid-count the way `0:9` → `0:10` would. Anything negative clamps to
 * `0:00` rather than rendering a minus sign into the HUD.
 */
export function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Which wave the strip should name.
 *
 * The snapshot's `wave` is *the wave that has been fought* — it is 0 before the
 * first one spawns and stays at the cleared number for the whole of the lull
 * after it. Naming that number during a lull would put "WAVE 0 / 8" on screen
 * for the ten seconds before the mission starts, and "WAVE 3 / 8" beside a
 * countdown to wave four.
 *
 * So the headline names the wave being **fought or awaited**: `wave` while one
 * is up, and the next one while the arena is quiet. `awaiting` is exactly
 * "`nextWaveAtTick` is present", which the server sends only during a lull.
 *
 * Clamped to `WAVE_COUNT` for the one tick between the last wave being cleared
 * and the mission finishing, where the sum would otherwise read "WAVE 9 / 8".
 */
export function waveOnScreen(wave: number, awaiting: boolean, total = WAVE_COUNT): number {
  return awaiting ? Math.min(wave + 1, total) : Math.max(1, wave);
}

/**
 * Which leg the strip should name, 1-based.
 *
 * `MissionSnapshot.leg` is the **0-based index of the waypoint being driven
 * to** and `legs` is how many waypoints the route has, so the display is one
 * more than the index — and clamped into the route, because a convoy that has
 * arrived reports the terminus and the label must not read "LEG 7 / 6".
 */
export function legOnScreen(leg: number, legs: number): number {
  if (legs <= 0) return 0;
  return Math.min(Math.max(1, leg + 1), legs);
}

/**
 * What the chip over the supply column says, per state.
 *
 * Here rather than in `GameHud.vue` for the reason every other derived string
 * on the mission strips is: the wire carries an enum (`ConvoyStatus`) so an
 * e2e case compares a server decision rather than a rendered phrase, and this
 * is the one place that turns the decision into words. A second copy in a
 * component is how a status ends up reading "Halted" in one place and "Under
 * attack" in another for the same tick.
 *
 * **Every stopped state ends in an exclamation mark and the one moving state
 * does not**, which is the whole of the urgency signal in the text itself —
 * colour carries the rest, and colour is not something every player has. That
 * is asserted against `convoyHalted` rather than against a list, so a fifth
 * state cannot be added punctuated as calm while the column sits still.
 *
 * `Waiting for you!` is deliberately an instruction rather than a description.
 * The column only rolls while a human is inside `CONVOY_ESCORT_RADIUS`, so a
 * player who has driven off is looking at a stationary vehicle and the useful
 * thing to tell them is what to do about it — "Standing by", which this said
 * while the state only meant *the mission has not begun*, would now be a
 * shrug at the one moment the player is costing themselves the run.
 */
export const CONVOY_STATUS_LABELS: Readonly<Record<ConvoyStatus, string>> = {
  waiting: 'Waiting for you!',
  rolling: 'Moving',
  'enemy-near': 'Enemy nearby!',
  'under-attack': 'Under attack!',
};

/**
 * The same thing as a function, for a status the client may not recognise.
 *
 * A build talking to a newer server can be sent a fifth state, and the honest
 * answer for one is the *neutral* label rather than a blank chip or the word
 * `undefined` floating over a tank. Exactly the fallback `Nameplate.titleId`
 * takes for a title this build has never heard of.
 */
export function convoyStatusLabel(status: ConvoyStatus | undefined): string {
  return (status && CONVOY_STATUS_LABELS[status]) || CONVOY_STATUS_LABELS.rolling;
}

/**
 * How many enemy tanks are still standing, **counted here rather than asked
 * for**.
 *
 * The snapshot already carries every tank's side and whether it is alive, so a
 * per-tick enemy count on the wire would be a second statement of something the
 * client is already holding — and the two would disagree on exactly the tick
 * that matters, the one where a wave's last tank dies.
 *
 * `friendly` is the client's own resolution of `red`/`blue` against the side it
 * was put on, so this stays true for a player on either side and never has to
 * learn a team id. Absent means friendly, matching the roster panel: the local
 * player's own row carries `friendly: true` and everything unstated is the
 * scene's own.
 */
export function enemiesLeft(
  players: readonly { friendly?: boolean; alive?: boolean }[],
): number {
  return players.filter((p) => p.friendly === false && p.alive !== false).length;
}

/**
 * Seconds until a dropped core walks itself home.
 *
 * `CTF_FLAG_RETURN_TICKS` is shared, so this is the same countdown the server
 * is running rather than a client-side approximation of it.
 */
export function flagReturnSeconds(droppedAtTick: number, nowTick: number): number {
  return secondsUntil(droppedAtTick + CTF_FLAG_RETURN_TICKS, nowTick);
}

/** How long a gate stays lit on the scope after a wave came out of it. */
export const GATE_FLASH_TICKS = Math.round(2.5 * TICK_RATE);

/**
 * How brightly the spawn gates are drawn, 1 the instant a wave arrives and 0
 * once the flash has run out.
 *
 * **Derived from the server tick, not from frame time.** The radar is redrawn
 * per frame, so a flash faded by `dt` would be a frame-rate-dependent value on
 * the HUD path — and, worse, would need somewhere to live between frames. What
 * is stored is the tick the wave started on; this is a subtraction.
 *
 * `undefined` for "no wave has started" answers 0, so the caller has one branch
 * rather than two.
 */
export function gateFlash(
  startedAtTick: number | undefined,
  nowTick: number,
  duration = GATE_FLASH_TICKS,
): number {
  if (startedAtTick === undefined || duration <= 0) return 0;
  const elapsed = nowTick - startedAtTick;
  if (elapsed < 0 || elapsed >= duration) return 0;
  return 1 - elapsed / duration;
}
